// Payouts > Weekly statement (Paystack plan 8, PR 14). Per company: what Zuttle
// owes it by EFT this week, which an admin pays and marks paid; what Paystack
// paid it at source; and its charter money. Only the first is owed.

import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import EmptyState from '../common/EmptyState';
import { AdminPayoutAPI } from '../../services/api';
import CompanyWeekRow, { stillOwed } from './CompanyWeekRow';
import { mondayOf, rands, fmtDate } from './money';

const Tile = ({ label, value, tone = 'text-gray-900', note }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-4">
    <p className="text-sm text-gray-500">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${tone}`}>{value}</p>
    {note && <p className="text-xs text-gray-400 mt-1">{note}</p>}
  </div>
);

// A 409 (the week changed since the page loaded) or a 422 (it owes less now)
// from "Mark paid" or Undo (#553): the numbers on the screen are stale.
const isStale = (e) => [409, 422].includes(e.response?.status);

const WeeklyStatement = () => {
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  // { companyId, companyName, amount, paidAmount, paidReference }. `amount` is
  // the whole `totalOwed`: "Mark paid" sets the week's payout to it, so on a
  // part-paid week it adds the rest (`paidAmount` is what was paid already, 0
  // for an unpaid week, and is sent as `expectedPaidCents`).
  const [payModal, setPayModal] = useState(null);
  const [payRef, setPayRef] = useState('');
  const [busy, setBusy] = useState(null);

  const thisMonday = mondayOf(new Date());
  const isCurrentWeek = weekStart === thisMonday;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await AdminPayoutAPI.getWeekly(weekStart);
      if (res.data.success) setData(res.data.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const shiftWeek = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(mondayOf(d));
  };

  const handleMarkPaid = async () => {
    if (!payModal) return;
    setBusy(payModal.companyId);
    try {
      await AdminPayoutAPI.markCompanyPaid(payModal.companyId, weekStart, payModal.amount, payRef, payModal.paidAmount);
      setPayModal(null); setPayRef('');
      await fetchData();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to mark as paid');
      // Stale numbers: close the dialog and reload the week. Any other error
      // (a bad reference) keeps the dialog open to fix it.
      if (isStale(e)) {
        setPayModal(null); setPayRef('');
        await fetchData();
      }
    } finally { setBusy(null); }
  };

  // Undo sends the paid total the row showed, so it removes only what the admin saw.
  const handleUnmark = async (c) => {
    setBusy(c.companyId);
    try {
      await AdminPayoutAPI.unmarkCompanyPaid(c.companyId, weekStart, c.paidAmount);
      await fetchData();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to undo');
      if (isStale(e)) await fetchData();
    } finally { setBusy(null); }
  };

  const handleExport = async () => {
    try {
      const res = await AdminPayoutAPI.weeklyExport(weekStart);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `zuttle-payouts-week-${weekStart}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed');
    }
  };

  const companies = data?.companies || [];
  const grandTotal = data?.grandTotal || 0;
  const totalPaid = data?.totalPaid || 0;
  const outstanding = grandTotal - totalPaid;
  const paidAtSourceTotal = data?.paidAtSourceTotal || 0;
  const charterTotal = data?.charterOperatorTotal || 0;
  const hasCharter = companies.some((c) => c.charter?.entries?.length);
  // Companies Zuttle pays by EFT this week: not one whose week owes less than R0 (#640).
  const owedCount = companies.filter((c) => c.totalOwed > 0).length;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <p className="text-gray-600">Pay each company what Zuttle owes it by EFT; they distribute to their drivers. Card payments Paystack paid to the operator at source are shown, not owed.</p>
        <button onClick={handleExport} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
          <Download size={16} /> Export CSV
        </button>
      </div>

      <div className="flex items-center justify-between bg-white rounded-lg border border-gray-200 px-4 py-3 mb-6">
        <button onClick={() => shiftWeek(-1)} className="p-2 rounded-lg hover:bg-gray-100" title="Previous week">
          <ChevronLeft size={20} />
        </button>
        <div className="text-center">
          <div className="font-semibold text-gray-900">
            {data ? `${fmtDate(data.weekStart)} – ${fmtDate(data.weekEnd)}` : 'Loading…'}
          </div>
          {isCurrentWeek && <div className="text-xs text-emerald-600 font-medium">Current week</div>}
        </div>
        <button onClick={() => shiftWeek(1)} disabled={isCurrentWeek}
          className={`p-2 rounded-lg ${isCurrentWeek ? 'text-gray-300' : 'hover:bg-gray-100'}`} title="Next week">
          <ChevronRight size={20} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Tile label="Total to pay out" value={rands(grandTotal)} note={`By EFT · ${owedCount} compan${owedCount === 1 ? 'y' : 'ies'}`} />
        <Tile label="Outstanding" value={rands(outstanding)} tone="text-yellow-600" />
        <Tile label="Paid" value={rands(totalPaid)} tone="text-green-600" />
      </div>
      {(paidAtSourceTotal !== 0 || hasCharter) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Tile label="Paid by Paystack at source" value={rands(paidAtSourceTotal)} note="Not owed: Paystack paid the operators' subaccounts" />
          <Tile label="Charter, operators' share" value={rands(charterTotal)} note="Not owed by EFT" />
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-gray-400">Loading…</div>
      ) : companies.length === 0 ? (
        <EmptyState icon={DollarSign} title="No payouts this week" message="No completed rides for this week yet." />
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100 mt-6">
          {companies.map((c) => (
            <CompanyWeekRow
              key={c.companyId}
              c={c}
              expanded={Boolean(expanded[c.companyId])}
              onToggle={() => setExpanded((e) => ({ ...e, [c.companyId]: !e[c.companyId] }))}
              busy={busy === c.companyId}
              onMarkPaid={() => {
                const partPaid = stillOwed(c) > 0;
                setPayModal({
                  companyId: c.companyId, companyName: c.companyName, amount: c.totalOwed,
                  paidAmount: partPaid ? c.paidAmount : 0, paidReference: partPaid ? c.paidReference : null,
                });
                setPayRef('');
              }}
              onUnmark={() => handleUnmark(c)}
            />
          ))}
        </div>
      )}

      {payModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setPayModal(null)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900">Mark as paid</h3>
            {payModal.paidAmount > 0 ? (
              <p className="text-sm text-gray-600 mt-1">
                {rands(payModal.paidAmount)} was marked paid for this week already. Confirm you've paid <span className="font-medium">{payModal.companyName}</span> the
                other {rands(payModal.amount - payModal.paidAmount)}. The week is then marked paid at {rands(payModal.amount)}.
                {payModal.paidReference ? ` Leave the reference blank to keep ${payModal.paidReference}.` : ''}
              </p>
            ) : (
              <p className="text-sm text-gray-600 mt-1">
                Confirm you've paid <span className="font-medium">{payModal.companyName}</span> {rands(payModal.amount)} for this week.
              </p>
            )}
            <label className="block text-sm font-medium text-gray-700 mt-4 mb-1">Payment reference (optional)</label>
            <input value={payRef} onChange={(e) => setPayRef(e.target.value)} placeholder="e.g. EFT ref / bank txn id"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" autoFocus />
            <div className="flex gap-3 mt-6">
              <button onClick={() => setPayModal(null)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={handleMarkPaid} disabled={busy === payModal.companyId}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">
                {busy === payModal.companyId ? 'Saving…' : 'Confirm paid'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeeklyStatement;
